import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { authService } from '../api';
import toast from 'react-hot-toast';
import { User, Phone, Mail, ShieldCheck } from 'lucide-react';
import AppSelect from '../components/ui/AppSelect'

function Profile() {
    const { user, refreshUser } = useAuth();
    const [formData, setFormData] = useState({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || ''
    });
    const [loading, setLoading] = useState(false);

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // Assuming we'd add updateProfile to authService
            toast.success('Profile updated successfully!');
        } catch (err) {
            toast.error('Failed to update profile.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <main className="container" style={{ padding: 'var(--spacing-xl) 0' }}>
                <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                    <header style={{ marginBottom: 'var(--spacing-xl)' }}>
                        <h2 style={{ fontSize: '2.5rem' }}>Personal Settings</h2>
                        <p className="text-muted">Manage your account information and preferences.</p>
                    </header>

                    <div className="flex flex-col gap-lg">
                        {/* Profile Info Section */}
                        <section style={{ backgroundColor: 'var(--color-card-bg)', padding: 'var(--spacing-xl)', borderRadius: '24px', boxShadow: 'var(--shadow-sm)',
                  color: 'var(--color-text-primary)' }}>
                            <div className="flex items-center gap-md mb-lg">
                                <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: 'var(--color-accent-peach)', display: 'flex', alignItems: 'center', justifyCenter: 'center', fontSize: '2rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0 }}>{user?.first_name} {user?.last_name}</h3>
                                    <p className="text-muted">{user?.email}</p>
                                </div>
                            </div>

                            <form onSubmit={handleUpdateProfile} className="flex flex-col gap-md">
                                <div className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-md)' }}>
                                    <div className="flex flex-col gap-xs">
                                        <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><User size={14} /> First Name</label>
                                        <input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
                                    </div>
                                    <div className="flex flex-col gap-xs">
                                        <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><User size={14} /> Last Name</label>
                                        <input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-xs">
                                    <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><Mail size={14} /> Email</label>
                                    <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
                                </div>

                                <div className="flex flex-col gap-xs">
                                    <label style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}><Phone size={14} /> Phone</label>
                                    <input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: '12px' }} />
                                </div>

                                <button className="btn btn-primary" style={{ alignSelf: 'flex-start', marginTop: 'var(--spacing-md)' }}>Save Changes</button>
                            </form>
                        </section>

                        {/* Security Section */}
                        <section style={{ backgroundColor: 'var(--color-card-bg)', padding: 'var(--spacing-xl)', borderRadius: '24px', boxShadow: 'var(--shadow-sm)',
                  color: 'var(--color-text-primary)' }}>
                            <h3 className="flex items-center gap-sm mb-md"><ShieldCheck size={20} /> Security</h3>
                            <p className="text-muted mb-lg">Password last changed 3 months ago.</p>
                            <button className="btn btn-outline">Change Password</button>
                        </section>

                        {/* Switch Campus Section (For Admin/Staff with multiple campuses) */}
                        {user?.user_campuses?.length > 1 && (
                            <section style={{ backgroundColor: 'var(--color-card-bg)', padding: 'var(--spacing-xl)', borderRadius: '24px', boxShadow: 'var(--shadow-sm)',
                  color: 'var(--color-text-primary)' }}>
                                <h3 className="flex items-center gap-sm mb-md">🏢 Switch Campus</h3>
                                <p className="text-muted mb-lg">Change your active working context. Your current campus is <strong>{user.campus_name}</strong>.</p>
                                <div className="flex gap-md">
                                    <AppSelect
                                        defaultValue={user.campus}
                                        id="campus-switch-select"
                                        style={{
                                            flex: 1, padding: '12px',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: '12px',
                                            backgroundColor: 'var(--color-card-bg)',
                  color: 'var(--color-text-primary)'
                                        }}
                                    >
                                        {user.user_campuses.map(c => (
                                            <option key={c.id} value={c.id}>{c.campus_name}</option>
                                        ))}
                                    </AppSelect>
                                    <button
                                        className="btn btn-primary"
                                        onClick={async () => {
                                            const newId = document.getElementById('campus-switch-select').value;
                                            if (Number(newId) === Number(user.campus)) return;
                                            const loadingToast = toast.loading('Switching campus...');
                                            try {
                                                await authService.switchCampus(newId);
                                                await refreshUser();
                                                toast.success('Switched campus successfully!', { id: loadingToast });
                                            } catch (err) {
                                                toast.error('Failed to switch campus.', { id: loadingToast });
                                            }
                                        }}
                                    >
                                        Switch
                                    </button>
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </main>
        </DashboardLayout>
    );
}

export default Profile;
